import { licenseMap } from 'pages/allinone/hooks/useLicense';
import { clmLicenseMap, sspmLicenseMap } from './SiteForm.schema';
import { ErrorCodes } from 'commonTypes';

export enum SITE_STATUS {
  Initializing = 'Initializing',
  Active = 'Active',
  Failed = 'Failed',
  Locked = 'Locked'
}

export enum SITE_TYPE {
  Site = 'Site',
  Account = 'Account',
  SiteManager = 'SiteManager'
}

export enum ACCOUNT_TYPE {
  Mssp = 'mssp',
  Direct = 'direct',
  Internal = 'internal',
  Sandbox = 'sandbox',
  TopHierarchy = 'top-hierarchy',
  FreeTrial = 'free-trial'
}

export const LICENSE_TYPE = {
  CLM_LICENSE: 1,
  SSPM_LICENSE: 2,
  EMAIL_LICENSE: 3,
  DOMAIN_FILTERING_LICENSE: 4,
  ESPM_LICENSE: 5,
  CVE_LICENSE: 6,
  WINDOWS_EVENTS_LICENSE: 7,
  FILE_MONITORING_LICENSE: 8,
  ANALYTICS_LICENSE: 9,
  CYOPS_LICENSE: 10
} as const;

export enum LICENSE_STATUS {
  Unknown = 0,
  Pending = 1,
  Success = 2,
  Failed = 3
}

export type LicenseConfig = {
  LicenseType: typeof LICENSE_TYPE[keyof typeof LICENSE_TYPE];
  LicenseArgs: string;
  LicenseStatus: LICENSE_STATUS;
};

export type LicensePayloadConfig = {
  type: typeof LICENSE_TYPE[keyof typeof LICENSE_TYPE];
  args: Record<string, string | boolean | number>;
};

export type Contact = {
  guid: string;
  name: string;
  email: string;
  phone: string;
};

export type Site = {
  guid: string;
  status: SITE_STATUS;
  cynetId: number;
  name: string;
  type: string;
  billingType?: SITE_BILLING_TYPE;
  comment?: string;
  updatedAt?: Date;
  contacts?: Contact[];
  parentGuid: string;
};

// Paying = Client
export enum SITE_BILLING_TYPE {
  Unknown = 'Unknown',
  Poc = 'Poc',
  Client = 'Client',
  Partner = 'Partner',
  Test = 'Test',
  Inactive = 'Inactive',
  Suspended = 'Suspended',
  Offline = 'Offline',
  Finish = 'Finish',
  TryAndBuy = 'TryAndBuy',
  IncidentResponse = 'IncidentResponse',
  NFR = 'NFR'
}

export enum SITE_BILLING_TYPE_INT {
  Unknown = -1,
  Poc = 0,
  Client = 1,
  Partner = 2,
  Test = 3,
  Inactive = 4,
  Suspended = 5,
  Offline = 6,
  Finish = 7,
  TryAndBuy = 8,
  IncidentResponse = 9,
  NFR = 10
}

export type Inputs = {
  siteType?: SITE_TYPE;
  cynetId?: number | string;
  name: string;
  billingType?: SITE_BILLING_TYPE;
  comment?: string;
  contacts: Contact[];
  packageName?: string;
};

export type InputsWithLicenseValue = {
  clmLicense: keyof typeof clmLicenseMap | '';
  sspmLicense: keyof typeof sspmLicenseMap | '';
  salesforceAccountId: string;
  salesForceIdValidity: boolean;
} & Inputs;

export type InputsWithLicenseArgs =
  | ((
      | {
          licenses: LicenseConfig[];
          siteType: SITE_TYPE.Site;
        }
      | { siteType: Exclude<Inputs['siteType'], SITE_TYPE.Site> }
    ) &
      Omit<Inputs, 'siteType'>)
  | Inputs;

export type NewContact = Omit<Contact, 'guid'>;

export type UpdatedInputs = {
  name: string;
  salesforceAccountId?: string;
  comment?: string;
  addContacts: NewContact[];
  updateContacts: Contact[];
  removeContacts: Contact[];
};

export type ContactsSet = {
  addContacts: NewContact[];
  updateContacts: Contact[];
  removeContacts: Contact[];
};

export type LicenseType = keyof typeof licenseMap;
export type LicenseTypeEnum = typeof licenseMap[LicenseType];

export type ClmLicenseInfo = {
  licenseType: number;
  licenseArgs: Record<string, unknown>;
  licenseName: LicenseType;
  licenseStatus?: number;
  clmLicenseName?: string;
  isLimited?: boolean;
};

export type OtherLicenseInfo = {
  licenseType: number;
  licenseArgs: Record<string, unknown>;
  licenseName: string;
  isLimited?: boolean;
};

export type LicenseInfo = ClmLicenseInfo | OtherLicenseInfo;

export type SiteLicenseAndPackageInfo = {
  siteGuid: string;
  contextSiteGuid: string;
  type: SITE_TYPE;
  packageName: string | null;
  licenses: LicenseInfo[];
  purchasedLicenses: string | null;
};

export type GetAllLicensesAndPackagesResponse = {
  data: SiteLicenseAndPackageInfo[];
};

export type ErrorMessage = {
  code: ErrorCodes;
  message: string;
};

export type GetAllLicensesAndPackagesResult = GetAllLicensesAndPackagesResponse | ErrorMessage;

export type GetAllLicensesAndPackagesVariables = {
  siteGuid: string;
  siteGuids: string[];
  enrichSitesAndDescendants: boolean;
};
